// src/encounter/lib/vital-limits.ts
//
// `vitalsRangeChecks` (ON on dev; Samer, 3 Nov evening P0: "the system accepts impossible values… no range checks"). ONE table of
// vitals entry ranges, by LOINC code, used by every vitals editor's EXISTING validation (the pre-visit grid, the Edit vitals dialog,
// the appointment panel's tab): an impossible value, or a diastolic not lower than the systolic, is refused on Save with the
// editor's own error display, the way a non-numeric value is refused today. Andrew, 4 Nov: "we will only do the ranges, not
// changing the UI".
// Limits NOT clinically signed off (Samer declined review). They are wide and block only impossible values. A ruling changes the
// numbers in VITAL_LIMITS only. Entry checks only: they never flag a stored reading.
import { VITAL_CODES } from '@/types/vitals-api';
import { isEncounterFlagOn } from '@/shared/lib/encounter-flags';

export interface VitalLimit {
  /** The name in the message. */
  label: string;
  /** Impossible outside this range: Save is refused. */
  min: number;
  max: number;
  /** The unit in the message ('' for none). */
  unit: string;
}

/** Entry ranges by LOINC code. NOT clinically signed off (see the header). */
export const VITAL_LIMITS: Readonly<Record<string, VitalLimit>> = {
  [VITAL_CODES.RESP_RATE]: { label: 'Resp rate', min: 0, max: 100, unit: '/min' },
  [VITAL_CODES.HEART_RATE]: { label: 'Pulse', min: 20, max: 300, unit: 'bpm' },
  [VITAL_CODES.TEMPERATURE]: { label: 'Temperature', min: 25, max: 45, unit: '°C' },
  [VITAL_CODES.SYSTOLIC]: { label: 'Systolic BP', min: 40, max: 300, unit: 'mmHg' },
  [VITAL_CODES.DIASTOLIC]: { label: 'Diastolic BP', min: 10, max: 200, unit: 'mmHg' },
  [VITAL_CODES.O2_SAT]: { label: 'O2 sat', min: 30, max: 100, unit: '%' },
  [VITAL_CODES.HEIGHT]: { label: 'Height', min: 20, max: 275, unit: 'cm' },
  [VITAL_CODES.WEIGHT]: { label: 'Weight', min: 0.2, max: 500, unit: 'kg' },
  [VITAL_CODES.HIP]: { label: 'Hip circumference', min: 20, max: 250, unit: 'cm' },
  [VITAL_CODES.WAIST]: { label: 'Waist circumference', min: 20, max: 250, unit: 'cm' },
  [VITAL_CODES.HCM]: { label: 'Head circumference', min: 18, max: 75, unit: 'cm' },
};

export const vitalsRangeChecksOn = (): boolean => isEncounterFlagOn('vitalsRangeChecks');

const num = (raw: string | undefined): number | null => {
  const v = (raw ?? '').trim();
  if (!v) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const fmt = (n: number) => String(n);
const withUnit = (n: number, unit: string) => (unit === '%' ? `${fmt(n)}%` : unit ? `${fmt(n)} ${unit}` : fmt(n));

/** The refusal for one value, or null when it is possible (or has no range, or is not a number). */
export function vitalRangeError(code: string, raw: string | undefined): string | null {
  const limit = VITAL_LIMITS[code];
  const n = num(raw);
  if (!limit || n == null) return null;
  if (n >= limit.min && n <= limit.max) return null;
  return `${limit.label} ${withUnit(n, limit.unit)} is outside the possible range (${fmt(limit.min)}–${withUnit(limit.max, limit.unit)}).`;
}

/** The first refusal in a code-keyed form (table order, then the BP pair); null when Save may go on. Switch OFF: null. */
export function vitalsRangeError(form: Readonly<Record<string, string | undefined>>): string | null {
  if (!vitalsRangeChecksOn()) return null;
  for (const code of Object.keys(VITAL_LIMITS)) {
    const error = vitalRangeError(code, form[code]);
    if (error) return error;
  }
  const sys = num(form[VITAL_CODES.SYSTOLIC]);
  const dia = num(form[VITAL_CODES.DIASTOLIC]);
  if (sys != null && dia != null && dia >= sys) return `Diastolic BP (${fmt(dia)}) must be lower than systolic BP (${fmt(sys)}).`;
  return null;
}
